<template>
  <section class="board">
    <!-- The scheduled AI pipeline that feeds this board -->
    <div v-if="pipeline || pipelineMissing" class="board-bar">
      <template v-if="pipeline">
        <div class="board-auto">
          <button
            type="button"
            class="board-switch"
            :class="{ on: pipeline.enabled }"
            role="switch"
            aria-label="AI auto-onboarding"
            :aria-checked="pipeline.enabled ? 'true' : 'false'"
            :disabled="saving"
            :title="pipeline.enabled ? 'Turn scheduled onboarding off' : 'Turn scheduled onboarding on'"
            @click="toggleEnabled()"><span></span></button>
          <div class="board-auto-copy">
            <strong>AI auto-onboarding <span class="board-auto-badge" :class="{ enabled: pipeline.enabled }">{{ pipeline.enabled ? 'On' : 'Off' }}</span></strong>
            <small>{{ scheduleLine }}</small>
          </div>
        </div>
        <label class="board-mode">
          <span>After validation</span>
          <select
            :value="importMode"
            :disabled="saving"
            title="Onboard automatically imports validated parts and approves the content written for them, so they go live without a review"
            @change="saveMode($event.target.value)">
            <option value="approval">Wait for my approval</option>
            <option value="auto">Onboard automatically</option>
          </select>
        </label>
        <router-link class="board-link" to="/users/pipelines">Schedule &amp; steps <span aria-hidden="true">↗</span></router-link>
      </template>
      <p v-else-if="pipelineMissing" class="board-note">
        Automatic discovery isn’t set up yet. You can still review and process parts on the board.
      </p>
    </div>

    <!-- What is moving the board right now — or what is waiting to move -->
    <div class="board-status" :class="{ running: status.running }" role="status">
      <template v-if="status.running">
        <span class="board-spinner board-spinner-lg" aria-hidden="true"></span>
        <div class="board-status-copy">
          <strong>{{ runningTitle }}</strong>
          <small>{{ runningDetail }}</small>
        </div>
        <button type="button" class="btn btn-outline-secondary" :disabled="stopping" @click="stop()">{{ stopping ? 'Stopping…' : 'Stop' }}</button>
      </template>
      <template v-else>
        <span class="board-status-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m9 5 7 7-7 7"/></svg></span>
        <div class="board-status-copy">
          <strong>{{ !loaded ? 'Loading pipeline' : waiting.total ? 'Ready for the next step' : 'No parts waiting to process' }}</strong>
          <small>{{ loaded ? waitingLine : 'Checking the latest progress…' }}</small>
        </div>
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="starting || !pipeline"
          title="Ask AI for new parts in every category, then move the whole board"
          @click="start('find')"><span aria-hidden="true">＋</span> Find new parts</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="starting || !waiting.total"
          title="Run every stage over what is waiting, earliest stage first"
          @click="start('process')">{{ starting ? 'Starting…' : 'Process board' }} <span v-if="waiting.total" class="board-action-count">{{ waiting.total }}</span><span aria-hidden="true">→</span></button>
      </template>
    </div>
    <p v-if="error" class="board-error" role="alert">{{ error }}</p>

    <div class="board-workspace">
      <div class="board-toolbar">
        <div class="board-toolbar-title"><h5>Parts pipeline</h5><span>{{ loaded ? cards.length : '…' }} parts</span><span class="board-connection" :class="{ online: liveConnected }" :title="liveConnected ? 'Stage and card changes arrive automatically.' : 'Live updates are reconnecting. The board refreshes automatically in the meantime.'"><i class="board-dot" aria-hidden="true"></i>{{ liveConnected ? 'Live' : 'Auto-refresh' }}</span></div>
        <label class="board-filter"><input v-model="hideEmpty" type="checkbox"> Hide empty stages</label>
      </div>
      <nav v-if="loaded" class="board-stage-map" aria-label="Pipeline stages">
        <button v-for="(column, index) in columns" :key="column.key" type="button" class="board-stage-step" :class="['col-' + column.key, { 'has-parts': column.cards.length }]" :title="column.hint" @click="focusColumn(column.key)">
          <span class="board-step-number">{{ index + 1 }}</span><span>{{ column.label }}</span><span class="board-step-count">{{ column.cards.length }}</span>
        </button>
      </nav>
      <div v-if="!loaded" class="board-loading" role="status"><span v-if="!error" class="board-spinner board-spinner-lg" aria-hidden="true"></span>{{ error ? 'The board couldn’t be loaded. Use Refresh to try again.' : 'Loading your parts pipeline…' }}</div>
      <div v-else class="board-columns" tabindex="0" role="region" aria-label="Parts by stage, scroll horizontally to view more">
        <div v-for="column in visibleColumns" :key="column.key" :ref="'column-' + column.key" class="board-col" :class="['col-' + column.key, { 'is-empty': !column.cards.length }]">
          <header class="board-col-head" :title="column.hint">
            <span class="board-stage-dot" aria-hidden="true"></span>
            <strong>{{ column.label }}</strong>
            <span
              v-if="status.stages && status.stages[column.key]"
              class="board-spinner"
              :title="`${jobLabel(status.stages[column.key].jobKey)} is working on this column`"></span>
            <button
              v-if="canRetryColumn(column)"
              type="button"
              class="btn btn-outline-warning board-retry-all"
              :disabled="!!retrying.all"
              title="Put every part here back where it stopped, then process the board"
              @click="retry()">{{ retrying.all ? 'Retrying…' : 'Retry all' }}</button>
            <span class="board-count">{{ column.cards.length }}</span>
          </header>
          <p class="board-col-hint">{{ column.hint || 'Parts at this stage of onboarding.' }}</p>
          <section
            v-for="group in column.groups"
            :key="group.key"
            class="board-group"
            :class="group.label ? ['is-titled', 'col-' + group.key] : null">
            <div v-if="group.label" class="board-group-head" :title="group.hint">
              <span class="board-stage-dot" aria-hidden="true"></span>
              <span>{{ group.label }}</span>
              <span class="board-group-count">{{ group.cards.length }}</span>
            </div>
            <transition-group tag="div" name="board-card" class="board-cards">
              <article
                v-for="card in group.visible"
                :key="card.key"
                class="board-card"
                :class="{ 'is-working': isWorking(card), 'is-clickable': !!card.candidateId }"
                :tabindex="card.candidateId ? 0 : -1"
                @click="open(card)"
                @keydown.enter.self="open(card)"
                @keydown.space.self.prevent="open(card)">
                <div class="board-card-top">
                  <img v-if="card.image" :src="card.image" alt="" loading="lazy">
                  <span v-else class="board-image-placeholder" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="6" y="6" width="12" height="12" rx="2"/><path d="M9 2v4m6-4v4M9 18v4m6-4v4M2 9h4m-4 6h4m12-6h4m-4 6h4"/></svg></span>
                  <div class="board-card-id">
                    <span class="board-tags">
                      <span class="board-type">{{ typeLabel(card.partType) }}</span>
                      <span v-if="card.source" class="board-src" :class="'src-' + card.source">{{ sourceLabel(card.source) }}</span>
                    </span>
                    <strong :title="card.title">{{ card.title }}</strong>
                    <time v-if="addedAgo(card.addedAt)" class="board-added" :datetime="card.addedAt" :title="addedDate(card.addedAt)">{{ addedAgo(card.addedAt) }}</time>
                  </div>
                  <span
                    v-if="card.unavailable"
                    class="board-flag"
                    title="Amazon can’t sell it right now. It imports as a hidden part and comes back when Amazon sells it again.">Not buyable</span>
                  <span v-else-if="card.price != null" class="board-price">${{ Number(card.price).toFixed(2) }}</span>
                </div>
                <p v-if="liveOf(card)" class="board-live" :class="'st-' + liveOf(card).status">
                  <span class="board-dot" aria-hidden="true"></span>
                  <span>{{ liveOf(card).label }}<template v-if="liveOf(card).detail"> · {{ liveOf(card).detail }}</template></span>
                </p>
                <p v-else-if="card.detail" class="board-detail">{{ card.detail }}</p>
                <div v-if="canImport(column, card) || canRetry(column, card)" class="board-card-foot">
                  <button
                    v-if="canImport(column, card)"
                    type="button"
                    class="btn btn-outline-success board-import"
                    :disabled="!!importing[card.key]"
                    :title="card.unavailable ? 'Import as a hidden part that comes back when Amazon sells it again' : 'Import into the live catalog'"
                    @click.stop="importCard(card)">{{ importing[card.key] ? 'Importing…' : 'Import' }}</button>
                  <button
                    v-if="canRetry(column, card)"
                    type="button"
                    class="btn btn-outline-warning board-import"
                    :disabled="!!retrying[card.key] || !!retrying.all"
                    title="Put it back where it stopped, then process the board"
                    @click.stop="retry(card)">{{ retrying[card.key] ? 'Retrying…' : 'Retry' }}</button>
                </div>
              </article>
            </transition-group>
            <button v-if="group.hidden" type="button" class="board-more" @click="$set(expanded, group.key, true)">
              +{{ group.hidden }} more
            </button>
            <button v-else-if="expanded[group.key] && group.cards.length > group.cap" type="button" class="board-more" @click="$delete(expanded, group.key)">
              Show fewer
            </button>
          </section>
          <div v-if="!column.cards.length" class="board-empty"><span aria-hidden="true">—</span><strong>No parts here yet</strong><small>Parts appear as they move through the pipeline.</small></div>
        </div>
      </div>
      <div v-if="loaded" class="board-footer"><span>Showing {{ visibleColumns.length }} of {{ columns.length }} stages</span><span>Recent activity · last 14 days</span></div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../../config'
import realtime from '../../../services/realtime'
import { describeCron } from '../../../services/schedule'

const PIPELINE_KEY = 'ai-onboarding'
const TOPIC = 'onboarding:board'
const TYPE_LABELS = { cpu: 'CPU', gpu: 'GPU', motherboard: 'Board', ram: 'RAM', storage: 'Storage', psu: 'PSU', case: 'Case' }
const SOURCE_LABELS = { ai_suggest: 'AI pick', manual: 'Asked', site_search: 'Search', search_console: 'Google', coverage: 'Coverage', search: 'Search' }
/** Columns that can grow long show their newest few until expanded. */
const DEFAULT_CAP = 40
/**
 * Where parts end up is history, not work: one "Recent" column on the far right,
 * a section per outcome, newest first, RECENT_CAP each until expanded.
 */
const RECENT_GROUPS = ['live', 'dropped']
const RECENT_CAP = 5
/** How long a finished step stays on its card before the card's own detail returns. */
const LIVE_LINGER_MS = 8000
/** Columns "Process board" can move forward (approval only in auto mode). */
const PROCESSABLE = ['queued', 'scraping', 'specs', 'content']
const WAITING_LABELS = { queued: 'queued', scraping: 'to scrape', specs: 'to research & validate', content: 'need content', approval: 'to import' }
const JOB_LABELS = {
  'suggest-part-requests': 'Suggesting parts',
  'onboard-part-requests': 'Finding parts on Amazon',
  'enrich-candidates': 'Scraping',
  'enrich-candidate-specs': 'Researching fields',
  'validate-candidates': 'Validating',
  'import-candidates': 'Importing',
  'generate-part-descriptions': 'Writing content'
}
const IDLE = { running: false, pipeline: null, jobs: [], stages: {} }

/**
 * The onboarding board: every part on its way into the catalog, as a card in
 * the column of the stage it has reached — suggested, queued, found on Amazon,
 * scraped, researched and validated, approved, written up, live. Cards move by
 * themselves over the onboarding:board socket topic, and whatever a job is
 * doing to a part right now shows on its card.
 *
 * Emits `open-candidate` (id) when a card with a candidate is clicked.
 */
export default {
  name: 'OnboardingBoard',
  data () {
    return {
      stages: [],
      cards: [],
      live: {},
      liveTimers: {},
      expanded: {},
      hideEmpty: true,
      importing: {},
      // card key (or 'all') -> a Retry request in flight
      retrying: {},
      loaded: false,
      now: Date.now(),
      liveConnected: false,
      cardsVersion: 0,
      statusVersion: 0,
      disposed: false,
      error: '',
      pipeline: null,
      pipelineMissing: false,
      saving: false,
      // What is moving the board (server-computed, pushed as onboarding:status).
      status: Object.assign({}, IDLE),
      starting: false,
      stopping: false,
      unsubscribeBoard: null,
      unsubscribePipelines: null,
      unsubscribeState: null,
      fallbackTimer: null
    }
  },
  computed: {
    visibleColumns () {
      const populated = this.columns.filter((column) => column.cards.length)
      return this.hideEmpty && populated.length ? populated : this.columns
    },
    columns () {
      // A column is one or more groups of cards; only Recent has more than one.
      const group = (stage, cap, titled) => {
        const cards = this.cards
          .filter((card) => card.stage === stage.key)
          .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
        const shown = this.expanded[stage.key] ? Infinity : cap
        return {
          key: stage.key,
          label: titled ? stage.label : null,
          hint: stage.hint,
          cap,
          cards,
          visible: cards.slice(0, shown),
          hidden: Math.max(cards.length - shown, 0)
        }
      }
      const work = this.stages
        .filter((stage) => !RECENT_GROUPS.includes(stage.key))
        .map((stage) => {
          const only = group(stage, DEFAULT_CAP, false)
          return Object.assign({}, stage, { cards: only.cards, groups: [only] })
        })
      const outcomes = RECENT_GROUPS
        .map((key) => this.stages.find((stage) => stage.key === key))
        .filter(Boolean)
        .map((stage) => group(stage, RECENT_CAP, true))
      if (!outcomes.length) return work
      return work.concat([{
        key: 'recent',
        label: 'Recent',
        hint: 'Newest first: parts that went live, and parts that were dropped',
        cards: [].concat(...outcomes.map((g) => g.cards)),
        groups: outcomes.filter((g) => g.cards.length)
      }])
    },
    importMode () {
      const config = (this.pipeline && this.pipeline.config) || {}
      return config.importMode === 'auto' ? 'auto' : 'approval'
    },
    /** Parts that "Process board" would move, by column. */
    waiting () {
      const stages = PROCESSABLE.concat(this.importMode === 'auto' ? ['approval'] : [])
      const by = {}
      let total = 0
      this.cards.forEach((card) => {
        if (!stages.includes(card.stage)) return
        by[card.stage] = (by[card.stage] || 0) + 1
        total++
      })
      return { total, by }
    },
    waitingLine () {
      const approvals = this.importMode === 'approval' ? this.cards.filter((card) => card.stage === 'approval').length : 0
      const parts = Object.keys(this.waiting.by).map((stage) => `${this.waiting.by[stage]} ${WAITING_LABELS[stage] || stage}`)
      const waitingText = this.waiting.total
        ? `${this.waiting.total} part${this.waiting.total === 1 ? '' : 's'} waiting to move — ${parts.join(' · ')}`
        : 'Nothing waiting to move'
      return approvals ? `${waitingText}. ${approvals} await${approvals === 1 ? 's' : ''} your approval.` : `${waitingText}.`
    },
    runningTitle () {
      const status = this.status
      if (status.pipeline) return status.pipeline.label
      const jobs = status.jobs || []
      if (jobs.length === 1) return `${this.jobLabel(jobs[0].jobKey)} is running`
      return `${jobs.length} onboarding jobs are running`
    },
    runningDetail () {
      const pipeline = this.status.pipeline
      if (pipeline && pipeline.step) {
        return `Step ${pipeline.step.position}${pipeline.stepsTotal ? ` of ${pipeline.stepsTotal}` : ''} — ${pipeline.step.label}`
      }
      if (pipeline) return 'Starting…'
      return 'Started from Stages or Scheduled Jobs — the board updates as it goes'
    },
    scheduleLine () {
      if (!this.pipeline) return ''
      const config = this.pipeline.config || {}
      const when = describeCron(this.pipeline.cron) || 'no schedule set'
      const perType = `${Number(config.count) || 2} new part${Number(config.count) === 1 ? '' : 's'} per category`
      return this.pipeline.enabled ? `Runs ${when} · ${perType}` : `Off — would run ${when} · ${perType}`
    },
    /** Card lookups for live activity, which names a request, candidate or part. */
    index () {
      const byRequest = {}
      const byCandidate = {}
      const byPart = {}
      this.cards.forEach((card) => {
        if (card.requestId) byRequest[card.requestId] = card.key
        if (card.candidateId) byCandidate[card.candidateId] = card.key
        if (card.partId && card.partType) byPart[`${card.partType}:${card.partId}`] = card.key
      })
      return { byRequest, byCandidate, byPart }
    }
  },
  mounted () {
    this.load()
    this.fetchPipeline()
    this.unsubscribeBoard = realtime.subscribe(TOPIC, {
      'onboarding:snapshot': (data) => { if (data && data.board) this.applyBoard(data.board) },
      'onboarding:card': (data) => this.upsert(data && data.card),
      'onboarding:activity': (data) => this.onActivity(data),
      'onboarding:status': (data) => { if (data && data.status) this.applyStatus(data.status) },
      'onboarding:error': (data) => {
        this.liveConnected = false
        this.error = (data && data.message) || 'Live board updates are unavailable. Retrying automatically.'
        this.load()
      }
    })
    // Schedule / on-off / approval edited elsewhere (e.g. the Pipelines page).
    this.unsubscribePipelines = realtime.subscribe('pipelines:all', {
      'pipeline:config': () => this.fetchPipeline()
    })
    this.unsubscribeState = realtime.onStateChange((state, connected, detail) => {
      this.liveConnected = realtime.isSubscribed(TOPIC)
      if (connected && (state === 'open' || (state === 'ready' && detail && detail.topic === TOPIC))) realtime.refresh(TOPIC)
    })
    this.liveConnected = realtime.isSubscribed(TOPIC)
    this.scheduleFallback()
  },
  beforeDestroy () {
    this.disposed = true
    if (this.unsubscribeBoard) this.unsubscribeBoard()
    if (this.unsubscribePipelines) this.unsubscribePipelines()
    if (this.unsubscribeState) this.unsubscribeState()
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer)
    Object.keys(this.liveTimers).forEach((key) => clearTimeout(this.liveTimers[key]))
  },
  methods: {
    addedAgo (value) {
      if (!value) return ''
      const added = new Date(value).getTime()
      if (!Number.isFinite(added)) return ''
      const minutes = Math.floor(Math.max(0, this.now - added) / 60000)
      if (minutes < 1) return 'Added just now'
      const count = minutes < 60 ? minutes : minutes < 1440 ? Math.floor(minutes / 60) : Math.floor(minutes / 1440)
      const unit = minutes < 60 ? 'minute' : minutes < 1440 ? 'hour' : 'day'
      return `Added ${count} ${unit}${count === 1 ? '' : 's'} ago`
    },
    addedDate (value) {
      return `Added to catalog ${new Date(value).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      })}`
    },
    async refresh () {
      await Promise.all([this.load(), this.fetchPipeline()])
    },
    focusColumn (key) {
      this.hideEmpty = false
      this.$nextTick(() => {
        const refs = this.$refs['column-' + key]
        const column = Array.isArray(refs) ? refs[0] : refs
        if (column) {
          const container = column.parentElement
          container.scrollLeft += column.getBoundingClientRect().left - container.getBoundingClientRect().left
        }
      })
    },
    headers () {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.usertoken}` }
    },
    errMsg (e) {
      return (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) || (e && e.message) || 'Request failed'
    },
    async load () {
      const cardsVersion = this.cardsVersion
      const statusVersion = this.statusVersion
      try {
        const { data } = await axios.get(`${apiDomain}admin/onboard/board?days=14`, { headers: this.headers() })
        if (this.disposed) return
        // A slow HTTP snapshot must not undo card moves or status events that
        // arrived while it was loading.
        this.applyBoard(data, {
          cards: cardsVersion === this.cardsVersion,
          status: statusVersion === this.statusVersion
        })
      } catch (e) {
        if (!this.disposed) this.error = this.errMsg(e)
      }
    },
    applyBoard (board, accept = { cards: true, status: true }) {
      if (!board) return
      this.now = Date.now()
      this.stages = Array.isArray(board.stages) ? board.stages : this.stages
      if (accept.cards && Array.isArray(board.cards)) {
        this.cards = board.cards
        this.cardsVersion++
      }
      if (accept.status && board.status) this.applyStatus(board.status)
      this.loaded = true
      this.error = ''
    },
    applyStatus (status) {
      const wasRunning = this.status.running
      this.status = status
      this.statusVersion++
      if (wasRunning !== status.running) this.scheduleFallback()
    },
    upsert (card) {
      if (!card || !card.key) return
      this.cardsVersion++
      const index = this.cards.findIndex((existing) => existing.key === card.key)
      if (index >= 0) this.cards.splice(index, 1, card)
      else this.cards.unshift(card)
    },
    onActivity (data) {
      if (!data) return
      const key = (data.request && this.index.byRequest[data.request]) ||
        (data.candidate && this.index.byCandidate[data.candidate]) ||
        (data.partId && this.index.byPart[`${data.partType}:${data.partId}`])
      if (!key) return
      this.$set(this.live, key, { label: data.label, detail: data.detail, status: data.status || 'running' })
      if (this.liveTimers[key]) clearTimeout(this.liveTimers[key])
      if (data.status === 'done' || data.status === 'failed') {
        this.liveTimers[key] = setTimeout(() => this.$delete(this.live, key), LIVE_LINGER_MS)
      }
    },
    liveOf (card) {
      return this.live[card.key] || null
    },
    isWorking (card) {
      const live = this.live[card.key]
      return !!live && live.status === 'running'
    },
    canImport (column, card) {
      return column.key === 'approval' && !!card.candidateId
    },
    canRetry (column, card) {
      return column.key === 'attention' && !!card.candidateId
    },
    canRetryColumn (column) {
      return column.key === 'attention' && column.cards.some((card) => card.candidateId)
    },
    /**
     * Needs attention: put one card (or, without one, the whole column) back
     * where it stopped, then process the board.
     */
    async retry (card) {
      const key = card ? card.key : 'all'
      if (this.retrying[key]) return
      this.$set(this.retrying, key, true)
      const statusVersion = this.statusVersion
      try {
        const { data } = await axios.post(`${apiDomain}admin/onboard/retry`,
          card ? { candidateIds: [card.candidateId] } : {}, { headers: this.headers() })
        // Same as start(): a streamed status may already have beaten the response.
        if (data.started && statusVersion === this.statusVersion) {
          this.applyStatus(Object.assign({}, IDLE, {
            running: true,
            pipeline: { runId: data.runId, label: 'Moving everything on the board forward', step: null }
          }))
          this.scheduleFallback()
        }
        this.error = data.message || ''
        await this.load()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.$delete(this.retrying, key)
      }
    },
    open (card) {
      if (card && card.candidateId) this.$emit('open-candidate', card.candidateId)
    },
    async importCard (card) {
      if (this.importing[card.key]) return
      this.$set(this.importing, card.key, true)
      try {
        await axios.post(`${apiDomain}part-candidates/${card.candidateId}/import`,
          { allowUnavailable: !!card.unavailable }, { headers: this.headers() })
        // Reconcile after a manual import as well as listening for its card event.
        await this.load()
      } catch (e) {
        this.error = `Import failed for ${card.title}: ${this.errMsg(e)}`
      } finally {
        this.$delete(this.importing, card.key)
      }
    },
    jobLabel (jobKey) {
      return JOB_LABELS[jobKey] || jobKey
    },
    typeLabel (type) {
      return TYPE_LABELS[type] || type || 'Part'
    },
    sourceLabel (source) {
      return SOURCE_LABELS[source] || source || ''
    },

    /* ---- pipeline ------------------------------------------------------- */

    async fetchPipeline () {
      try {
        const { data } = await axios.get(`${apiDomain}pipelines/${PIPELINE_KEY}`, { headers: this.headers() })
        this.pipeline = (data && data.pipeline) || null
        this.pipelineMissing = !this.pipeline
      } catch (e) {
        this.pipeline = null
        this.pipelineMissing = !!(e && e.response && e.response.status === 404)
        if (!this.pipelineMissing) this.error = this.errMsg(e)
      }
    },
    async savePipeline (payload) {
      this.saving = true
      try {
        const { data } = await axios.post(`${apiDomain}pipelines/${PIPELINE_KEY}`, payload, { headers: this.headers() })
        if (data && data.pipeline) this.pipeline = data.pipeline
        this.error = ''
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.saving = false
      }
    },
    toggleEnabled () {
      if (this.pipeline) this.savePipeline({ enabled: !this.pipeline.enabled })
    },
    saveMode (mode) {
      if (!this.pipeline) return
      this.savePipeline({ config: Object.assign({}, this.pipeline.config || {}, { importMode: mode }) })
    },
    /**
     * process: every stage takes what is waiting in its column, earliest first.
     * find:    ask AI for new parts first, then the same stages.
     */
    async start (mode) {
      if (this.starting || this.status.running) return
      this.starting = true
      const statusVersion = this.statusVersion
      try {
        const { data } = await axios.post(`${apiDomain}admin/onboard/run`, { mode }, { headers: this.headers() })
        // The first streamed step may beat the HTTP response. Never overwrite it
        // with "Starting…"; retain the run ID so Stop works during startup too.
        if (statusVersion === this.statusVersion) {
          this.applyStatus(Object.assign({}, IDLE, {
            running: true,
            pipeline: { runId: data.runId, label: mode === 'find' ? 'Finding new parts and moving the board' : 'Moving everything on the board forward', step: null }
          }))
        }
        this.error = ''
        this.scheduleFallback()
        await this.load()
      } catch (e) {
        const body = e && e.response && e.response.data
        if (body && body.status) this.applyStatus(body.status)
        this.error = this.errMsg(e)
      } finally {
        this.starting = false
      }
    },
    /** Stop the board pipeline (it halts before its next step), or the running stage jobs. */
    async stop () {
      if (this.stopping) return
      this.stopping = true
      try {
        const pipeline = this.status.pipeline
        if (pipeline && pipeline.runId) {
          await axios.post(`${apiDomain}pipelines/runs/${pipeline.runId}/cancel`, {}, { headers: this.headers() })
        } else {
          await Promise.all((this.status.jobs || []).map((job) =>
            axios.post(`${apiDomain}jobs/runs/${job.runId}/cancel`, {}, { headers: this.headers() })))
        }
        this.error = ''
        await this.load()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.stopping = false
      }
    },

    /** Reconcile missed events even on an open socket; poll faster during a run. */
    scheduleFallback () {
      if (this.fallbackTimer) clearTimeout(this.fallbackTimer)
      if (this.disposed) return
      this.fallbackTimer = setTimeout(async () => {
        this.now = Date.now()
        if (!realtime.isSubscribed(TOPIC)) {
          realtime.refresh(TOPIC)
          await this.fetchPipeline()
        }
        await this.load()
        this.scheduleFallback()
      }, this.status.running ? 5000 : 20000)
    }
  }
}
</script>

<style scoped>
.board { --board-muted: #9198aa; --board-border: rgba(67,72,87,0.9); display: grid; gap: 16px; min-width: 0; margin-bottom: 20px; font-size: 13px; color: #cfd2da; }
.board *, .board *::before, .board *::after { box-sizing: border-box; }
.board button, .board select { font-family: inherit; }
.board-status .btn { display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 36px; border-radius: 6px; font-size: 12px; font-weight: 700; }
.board-import { margin-left: auto; padding: 4px 10px; font-size: 11px; }
.board-retry-all { padding: 2px 8px; font-size: 11px; line-height: 1.5; }
.board button { cursor: pointer; }
.board button:disabled, .board select:disabled { opacity: 0.45; cursor: not-allowed; }
.board button:focus-visible, .board select:focus-visible, .board input:focus-visible, .board a:focus-visible, .board [tabindex]:focus-visible { outline: 2px solid #1997c6; outline-offset: 3px; }
.board-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 16px 24px; padding: 16px 20px; border: 1px solid var(--board-border); border-radius: 8px; background: rgba(8,11,17,0.62); }
.board-auto { display: flex; align-items: center; gap: 12px; min-width: 0; }
.board-auto-copy { display: grid; gap: 5px; }
.board-auto-copy strong { display: flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 600; }
.board-auto-copy small { color: var(--board-muted); font-size: 11px; line-height: 1.5; }
.board-auto-badge { padding: 2px 6px; border-radius: 4px; background: rgba(67,72,87,0.45); color: #aeb6c8; font-size: 10px; font-weight: 500; }
.board-auto-badge.enabled { color: #8fe8c4; background: rgba(27,201,142,0.12); }
.board-switch { position: relative; flex: 0 0 auto; width: 36px; height: 21px; padding: 0; border: 1px solid #434857; border-radius: 99px; background: rgba(8,11,17,0.72); transition: background 0.15s; }
.board-switch span { position: absolute; top: 3px; left: 3px; width: 13px; height: 13px; border-radius: 50%; background: #9198aa; transition: transform 0.15s; }
.board-switch.on { border-color: #1bc98e; background: rgba(27,201,142,0.18); }
.board-switch.on span { transform: translateX(15px); background: #1bc98e; }
.board-mode { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin: 0 0 0 auto; }
.board-mode > span { color: var(--board-muted); font-size: 11px; }
.board-mode select { max-width: 100%; height: 34px; padding: 0 10px; border: 1px solid #434857; border-radius: 6px; background: rgba(8,11,17,0.72); color: #cfd2da; color-scheme: dark; font-size: 12px; }
.board-link { display: inline-flex; align-items: center; gap: 8px; color: #7fd0ee; font-size: 11px; text-decoration: none; white-space: nowrap; }
.board-link:hover { color: #eef8fc; }
.board-link span { font-size: 16px; }
.board-note { margin: 0; color: var(--board-muted); }
.board-status { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 20px; border: 1px solid rgba(25,151,198,0.32); border-radius: 8px; background: linear-gradient(110deg, rgba(25,151,198,0.09), rgba(8,11,17,0.62) 70%); }
.board-status.running { border-color: rgba(25,151,198,0.55); }
.board-status-icon { display: grid; place-items: center; width: 38px; height: 38px; border: 1px solid rgba(25,151,198,0.35); border-radius: 8px; background: rgba(25,151,198,0.1); color: #7fd0ee; }
.board-status-icon svg { width: 21px; height: 21px; }
.board-status-copy { display: grid; flex: 1 1 260px; gap: 5px; min-width: 0; }
.board-status-copy strong { color: #e7e9ee; font-size: 15px; font-weight: 600; }
.board-status-copy small { color: #a4acbc; font-size: 12px; line-height: 1.6; }
.board-action-count { display: inline-grid; place-items: center; min-width: 21px; height: 20px; padding: 0 4px; border-radius: 4px; background: #0002; font-size: 11px; }
.board-error { margin: 0; padding: 12px 16px; border: 1px solid rgba(232,79,106,0.5); border-radius: 8px; background: rgba(90,34,48,0.42); color: #f3c6cf; }
.board-workspace { min-width: 0; padding: 20px; border: 1px solid var(--board-border); border-radius: 8px; background: rgba(8,11,17,0.42); }
.board-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; }
.board-toolbar-title { display: flex; align-items: center; gap: 12px; }
.board-toolbar-title h5 { margin: 0; color: #dfe4ee; font-size: 15px; font-weight: 600; }
.board-toolbar-title > span { border-left: 1px solid #434857; padding-left: 12px; color: var(--board-muted); font-size: 12px; }
.board-toolbar-title { flex-wrap: wrap; }
.board-toolbar-title .board-connection { display: inline-flex; align-items: center; gap: 6px; color: #e0c07e; font-size: 11px; }
.board-toolbar-title .board-connection.online { color: #8fe8c4; }
.board-filter { display: inline-flex; align-items: center; gap: 8px; margin: 0; color: #aeb6c8; font-size: 12px; cursor: pointer; }
.board-filter input { width: 14px; height: 14px; accent-color: #1997c6; }
.board-stage-map { display: flex; flex-wrap: wrap; gap: 6px; padding-bottom: 20px; }
.board-stage-step { display: inline-flex; align-items: center; gap: 6px; padding: 6px 8px; border: 1px solid transparent; border-radius: 6px; background: rgba(13,18,28,0.6); color: #9198aa; font-size: 10px !important; text-align: left; }
.board-stage-step:hover { border-color: #1997c6; background: rgba(25,151,198,0.1); }
.board-step-number { color: #8f98aa; font-size: 9px; }
.board-step-count { padding: 0 4px; border-radius: 3px; color: #cfd2da; background: rgba(67,72,87,0.45); font-size: 10px; font-weight: 600; }
.board-stage-step.has-parts { color: #dfe4ee; border-color: #434857; }
.board-stage-step.has-parts .board-step-count { color: #9bdff5; background: rgba(25,151,198,0.14); }
.board-columns { display: flex; gap: 14px; min-width: 0; overflow-x: auto; align-items: stretch; padding: 2px 2px 14px; }
.board-col { --stage-color: #7fd0ee; display: flex; flex: 1 0 264px; flex-direction: column; gap: 12px; min-width: 0; max-width: 360px; min-height: 340px; padding: 14px; border: 1px solid rgba(67,72,87,0.62); border-radius: 8px; background: rgba(8,11,17,0.5); }
.board-col.is-empty { flex: 1 0 210px; }
.col-specs { --stage-color: #c4a7f5; }
.col-approval { --stage-color: #e0c07e; }
.col-content { --stage-color: #7fd0ee; }
.col-attention { --stage-color: #f49aaa; }
.col-live { --stage-color: #1bc98e; }
.col-dropped { --stage-color: #8f98aa; }
.col-recent { --stage-color: #9fb3c8; }
.board-col-head { display: flex; align-items: center; gap: 8px; }
.board-stage-dot { flex: 0 0 auto; width: 7px; height: 7px; border-radius: 50%; background: var(--stage-color); }
.board-col-head strong { flex: 1; color: #dfe4ee; font-size: 12px; font-weight: 600; }
.board-count { display: inline-grid; place-items: center; min-width: 23px; height: 22px; padding: 0 6px; border: 1px solid rgba(67,72,87,0.7); border-radius: 6px; background: rgba(67,72,87,0.35); color: var(--stage-color); font-size: 11px; font-weight: 600; }
.board-col-hint { min-height: 32px; margin: -4px 0 1px; color: #8f98aa; font-size: 11px; line-height: 1.5; }
.board-cards { display: grid; align-content: start; gap: 10px; }
/* Recent: one section per outcome, each with its own heading, count and more/fewer. */
.board-group { display: grid; align-content: start; gap: 10px; }
.board-group.is-titled + .board-group.is-titled { margin-top: 4px; padding-top: 14px; border-top: 1px solid #ffffff12; }
.board-group-head { display: flex; align-items: center; gap: 7px; margin: 0; color: #c9d1de; font-size: 10.5px; font-weight: 600; line-height: 18px; letter-spacing: 0.06em; text-transform: uppercase; }
.board-group-count { min-width: 20px; margin-left: auto; padding: 0 6px; border-radius: 5px; background: rgba(67,72,87,0.35); color: var(--stage-color); font-size: 10.5px; letter-spacing: 0; text-align: center; }
/* Card: image | tags + name | price, then one status line under the name, then
   actions only when there are any. The left edge carries the column's colour. */
.board-card { position: relative; display: grid; gap: 8px; min-width: 0; padding: 11px 12px 11px 14px; border: 1px solid rgba(67,72,87,0.55); border-radius: 8px; background: linear-gradient(180deg, rgba(17,23,34,0.96), rgba(12,16,25,0.96)); box-shadow: inset 2px 0 0 var(--stage-color), 0 1px 2px #0003; transition: border-color 0.2s, background 0.2s, box-shadow 0.2s; }
.board-card.is-clickable { cursor: pointer; }
.board-card.is-clickable:hover { border-color: rgba(127,208,238,0.45); background: linear-gradient(180deg, rgba(22,31,45,0.98), rgba(15,21,32,0.98)); }
.board-card.is-clickable:focus-visible { outline: 2px solid rgba(127,208,238,0.6); outline-offset: 1px; }
.board-card.is-working { border-color: rgba(25,151,198,0.75); box-shadow: inset 2px 0 0 #1997c6, 0 0 0 3px rgba(25,151,198,0.12); }
.board-card-top { display: grid; grid-template-columns: 36px minmax(0, 1fr) auto; align-items: start; gap: 10px; min-width: 0; }
.board-card-top img, .board-image-placeholder { width: 36px; height: 36px; padding: 2px; border-radius: 6px; background: #fff; object-fit: contain; }
.board-image-placeholder { display: grid; place-items: center; padding: 0; background: rgba(25,151,198,0.08); color: #7fd0ee; }
.board-image-placeholder svg { width: 22px; height: 22px; }
.board-card-id { display: grid; gap: 4px; min-width: 0; }
.board-added { color: var(--board-muted); font-size: 10px; line-height: 1.4; }
.board-card-id strong { display: -webkit-box; overflow: hidden; color: #e7ebf3; font-size: 12.5px; font-weight: 500; line-height: 1.4; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.board-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.board-type, .board-src { padding: 1px 5px; border-radius: 3px; font-size: 9.5px; font-weight: 600; line-height: 1.5; letter-spacing: 0.01em; }
.board-type { color: #7fd0ee; background: rgba(25,151,198,0.12); }
.board-src { color: #aeb6c8; background: rgba(67,72,87,0.25); }
.board-src.src-ai_suggest { color: #c4a7f5; background: rgba(196,167,245,0.1); }
/* Status under the name, lined up with the text rather than the image. */
.board-live, .board-detail { display: flex; align-items: baseline; gap: 6px; margin: 0 0 0 46px; font-size: 11px; line-height: 1.45; overflow-wrap: anywhere; }
.board-live { color: #9bdff5; }
.board-live.st-done { color: #8fe8c4; }
.board-live.st-failed, .col-attention .board-detail { color: #f49aaa; }
.board-detail { color: #8f98aa; }
.board-dot { flex: 0 0 auto; width: 6px; height: 6px; border-radius: 50%; background: currentColor; transform: translateY(-1px); }
.board-live.st-running .board-dot { animation: board-pulse 1.2s ease-in-out infinite; }
@keyframes board-pulse { 50% { opacity: 0.35; } }
.board-card-foot { display: flex; justify-content: flex-end; gap: 6px; margin-left: 46px; }
.board-price { padding-top: 1px; color: #e9d86a; font-size: 12.5px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.board-flag { padding: 1px 6px; border: 1px solid rgba(224,192,126,0.35); border-radius: 999px; color: #e0c07e; font-size: 10px; font-weight: 600; white-space: nowrap; cursor: help; }
.board-more { padding: 8px; border: 1px dashed #434857; border-radius: 5px; background: transparent; color: #7fd0ee; font-size: 11px !important; }
.board-empty { display: flex; flex: 1; flex-direction: column; align-items: center; justify-content: center; gap: 8px; min-height: 180px; padding: 15px; border: 1px dashed rgba(67,72,87,0.5); border-radius: 7px; color: #9198aa; text-align: center; }
.board-empty > span { display: grid; place-items: center; width: 30px; height: 30px; border: 1px solid #434857; border-radius: 8px; }
.board-empty strong { font-size: 12px; font-weight: 500; }
.board-empty small { max-width: 170px; color: #8f98aa; font-size: 11px; line-height: 1.6; }
.board-loading { display: flex; align-items: center; justify-content: center; gap: 12px; min-height: 250px; color: var(--board-muted); }
.board-footer { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px; padding-top: 12px; color: #8f98aa; font-size: 10px; }
.board-spinner, .board-live.st-running .board-dot { flex: 0 0 auto; width: 10px; height: 10px; border: 2px solid rgba(25,151,198,0.3); border-top-color: #16b4ed; border-radius: 50%; background: none; animation: board-spin 0.8s linear infinite; }
.board-spinner-lg { width: 18px; height: 18px; }
@keyframes board-spin { to { transform: rotate(360deg); } }
.board-card-enter-active, .board-card-leave-active { transition: opacity 0.3s, transform 0.3s; }
.board-card-enter, .board-card-leave-to { opacity: 0; transform: translateY(-6px); }
.board-card-move { transition: transform 0.3s; }
@media (max-width: 700px) {
  .board-bar, .board-status, .board-workspace { padding: 14px; }
  .board-mode { margin-left: 0; }
  .board-status-icon { display: none; }
  .board-status-copy { flex-basis: 100%; }
  .board-status .btn { flex: 1; }
  .board-col { flex-basis: 260px; }
  .board-stage-map { gap: 5px; }
}
@media (prefers-reduced-motion: reduce) {
  .board *, .board *::before, .board *::after { animation: none !important; transition: none !important; }
}
</style>
